import React, { useContext } from 'react'
import { OffreModel } from '../types/OffreModel'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import styles from '../../../styles'
import { fontFamilyMonserrat, gecinaBlueMain, GRIS, WHITE } from '../../../theme'
import SvgMapPin from '../../../icons/SvgMapPin'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { useTheme } from '@mui/material'
import SwiperMobileAvecFleches from '../../../components/swiper/SwiperMobileAvecFleches'
import SvgLesPlus from '../../../icons/SvgLesPlus'
import { Element } from 'react-scroll'
import { menuAncresEnum } from '../enums/menuAncresEnum'

const useStyles = makeStyles<{ isLargeScreen: boolean, isTabletteScreen: boolean }>()(
	(theme, { isLargeScreen, isTabletteScreen }) => ({
		residenceContainer: {
			padding: isLargeScreen ? '103px 65px 0' : '74px 30px 0',
			backgroundColor: GRIS,
			borderRadius: isTabletteScreen ? '30px 30px 0 0' : 0,
			marginTop: isTabletteScreen ? -30 : 0,
			zIndex: 2,
			position: 'relative'
		},
		tag: {
			background: theme.palette.primary.main,
			borderRadius: 21,
			...styles(theme).text6,
			color: WHITE,
			padding: '7px 12.5px',
			height: 'fit-content',
			margin: 0
		},
		infoContainer: {
			marginBottom: isLargeScreen ? 29 : 18,
			marginTop: isLargeScreen ? 16 : 26
		},
		titleContainer: {
			margin: 0,
			...styles(theme).title0,
			fontSize: isLargeScreen ? 45 : 30,
			marginBottom: isLargeScreen ? 0 : 11
		},
		intituleCrm: {
			color: theme.palette.primary.main
		},
		adresse: {
			fontSize: isLargeScreen ? 18 : 16,
			fontFamily: fontFamilyMonserrat,
			fontWeight: 500,
			color: gecinaBlueMain,
			textAlign: isLargeScreen ? 'end' : 'left'
		},
		adresseContainer: {
			'& svg': {
				verticalAlign: 'middle',
				marginRight: isLargeScreen ? 10 : 13
			},
			'& span': {
				verticalAlign: 'middle'
			}
		},
		detailsContainer: {
			background: WHITE,
			borderRadius: 30,
			padding: isLargeScreen ? '31px 96px 34px 35px' : '26px 25px'
		},
		swiperContainer: {
			width: isLargeScreen ? 375 : '100%',
			height: isLargeScreen ? 308 : 'auto',
			aspectRatio: isLargeScreen ? 'unset' : '320/263',
			marginRight: isLargeScreen ? 115 : 0,
			marginBottom: isLargeScreen ? 0 : 18
		},
		introduction: {
			...styles(theme).text27,
			fontSize: isLargeScreen ? 16 : 14,
			lineHeight: isLargeScreen ? '28px' : '24px',
			fontWeight: 500
		},
		lesPlus: {
			...styles(theme).text0,
			fontSize: 16,
			marginTop: isLargeScreen ? 25 : 35,
			marginBottom: isLargeScreen ? 30 : 35,
			whiteSpace: 'break-spaces'
		},
		svgLesPlus: {
			verticalAlign: 'middle'
		},
		lesPlusListeContainer: {
			marginBottom: isLargeScreen ? 8 : 19
		},
		lesPlusContainer: {
			marginBottom: isLargeScreen ? 25 : 18
		},
		lesPlusPicto: {
			height: isLargeScreen ? 30 : 20,
			maxWidth: 30,
			objectFit: 'contain',
			marginRight: isLargeScreen ? 15 : 9
		},
		lesPlusTexte: {
			...styles(theme).text1,
			fontSize: isLargeScreen ? 13 : 11,
			width: isLargeScreen ? 'unset' : '70%',
			margin: 0
		},
		decouvrirResidence: {
			borderRadius: 25
		}
	})
)

interface AcceptingProps {
	offre: OffreModel
}

type ResidenceProps = AcceptingProps

const Residence: React.FC<ResidenceProps> = (
	{
		offre
	}
) => {
	const { isLargeScreen, isTabletteScreen } = useContext(ResponsiveContext)
	const { classes } = useStyles({ isLargeScreen, isTabletteScreen })
	const theme = useTheme()

	return (
		<Element name={menuAncresEnum.RESIDENCE} style={{ width: '100%' }}>
			<Grid container item xs={12} className={classes.residenceContainer}>
				{/* Tag de la résidence */}
				<Grid container item xs={12}>
					<p className={classes.tag}>
						<FormattedMessage
							id="offre.residence.tag"
							defaultMessage="RÉSIDENCE"
							description="Tag du bloc"
						/>
					</p>
				</Grid>

				{/* Bloc contenant le nom de la résidence et l'adresse */}
				<Grid container item md={12} alignItems="center" className={classes.infoContainer}>
					<Grid item xs={12} md={8}>
						<h2 className={classes.titleContainer}>
							{offre.residence.surTitre} <span className={classes.intituleCrm}>{offre.residence.intituleCRM}</span>
						</h2>
					</Grid>

					<Grid container item xs={12} md={4} justifyContent={isLargeScreen ? 'flex-end' : 'flex-start'} className={classes.adresse}>
						<div className={classes.adresseContainer}>
							<SvgMapPin color={theme.palette.primary.main} width={17} height={21} />
							<span>
								{offre.residence.adresse}
							</span>
							{isLargeScreen ? <br /> : <> </>}
							<span>{offre.residence.ville} {offre.residence.codePostal}</span>
						</div>
					</Grid>
				</Grid>

				{/* Bloc contenant les photos de la résidence et les plus */}
				<Grid
					container
					item
					xs={12}
					className={classes.detailsContainer}
					alignItems="flex-start"
					direction={isLargeScreen ? 'row' : 'column'}
				>
					{/* Bloc contenant les photos */}
					<div className={classes.swiperContainer}>
						<SwiperMobileAvecFleches photos={offre.residence.photos} />
					</div>

					{/* Bloc contenant les plus de la résidence */}
					<Grid container item xs>
						<Grid container item xs={12} className={classes.introduction}>
							{offre.residence.lesPlus.introduction}
						</Grid>

						<Grid container item xs={12} alignItems="center" className={classes.lesPlus}>
							<FormattedMessage
								id="offre.residence.lesPlus"
								defaultMessage="Les {image} de votre résidence"
								description="Texte pour les plus"
								values={{
									image: <SvgLesPlus color={theme.palette.primary.main} className={classes.svgLesPlus} width={isLargeScreen ? 30 : 36} height={isLargeScreen ? 30 : 36} />
								}}
							/>
						</Grid>

						<Grid container item xs={12} className={classes.lesPlusListeContainer}>
							{
								offre.residence.lesPlus.lesPlus.map(
									(lesPlus) => (
										<Grid
											container
											item
											xs={6}
											sm={3}
											md={4}
											lg={3}
											alignItems="center"
											key={lesPlus.texte}
											className={classes.lesPlusContainer}
											wrap="nowrap"
										>
											<img src={lesPlus.picto} alt={lesPlus.texte} className={classes.lesPlusPicto} />
											<p className={classes.lesPlusTexte}>{lesPlus.texte}</p>
										</Grid>
									)
								)
							}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Element>
	)
}

export default Residence
